import { motion } from "framer-motion";
import { DeviceMobile, MagnifyingGlass, CursorClick } from "@phosphor-icons/react";

const services = [
  {
    id: "mobile-first",
    title: "Mobile-First Design",
    desc: "60% of your customers are searching on their phones. We build sites that look incredible and load instantly on mobile.",
    icon: DeviceMobile,
  },
  {
    id: "seo-optimized",
    title: "SEO-Optimized",
    desc: "Built cleanly so Google can easily read what you do and where you operate.",
    icon: MagnifyingGlass,
  },
  {
    id: "built-to-convert",
    title: "Built to Convert",
    desc: "Simple quote forms and \u201Cclick-to-call\u201D buttons so customers can reach you without thinking.",
    icon: CursorClick,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 sm:py-28 md:py-36"
    >
      <SectionHeader
        eyebrow="What We Do"
        title="Design. Trust. Visibility."
        description="Modern websites built for local service businesses."
        align="center"
      />

      <div className="mt-14 sm:mt-20 max-w-[1180px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          {services.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, index }) {
  const Icon = service.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`service-card-${service.id}`}
      className="group relative rounded-2xl border border-white/[0.07] bg-[#0e0e10] hover:bg-[#121215] hover:border-white/[0.14] transition-all duration-500 p-8 sm:p-9 md:p-10 min-h-[220px] flex flex-col"
    >
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 text-white group-hover:border-white/20 group-hover:bg-white/[0.06] transition-colors">
        <Icon size={20} weight="regular" />
      </span>

      <h3 className="mt-8 sm:mt-10 font-display text-xl sm:text-[22px] tracking-tight text-white leading-snug">
        {service.title}
      </h3>
      <p className="mt-2.5 text-[14px] sm:text-[14.5px] text-zinc-400 leading-relaxed">
        {service.desc}
      </p>

      <div className="mt-auto pt-7">
        <div className="h-px w-8 bg-white/15 group-hover:w-14 group-hover:bg-brand-400/70 transition-all duration-500" />
      </div>
    </motion.article>
  );
}

export function SectionHeader({ eyebrow, title, description, align = "left" }) {
  const wrap =
    align === "center"
      ? "text-center max-w-2xl mx-auto px-5 md:px-10 lg:px-14"
      : "max-w-3xl";
  const eyebrowCls =
    align === "center"
      ? "text-[11px] tracking-[0.22em] uppercase text-zinc-400 inline-flex items-center gap-3 justify-center"
      : "text-[11px] tracking-[0.22em] uppercase text-zinc-400 flex items-center gap-3";
  return (
    <div className={wrap}>
      <div className={eyebrowCls}>
        <span className="block w-6 h-px bg-zinc-600" />
        {eyebrow}
      </div>
      <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.05]">
        {title}
      </h2>
      {description && (
        <p
          className={`mt-5 text-zinc-400 text-[15px] sm:text-base leading-relaxed ${
            align === "center" ? "mx-auto" : ""
          }`}
        >
          {description}
        </p>
      )}
    </div>
  );
}
